import JSZip from 'jszip';
import { enqueueSnackbar } from '~/utils/notifications';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    UploadDICOMRequestBody,
    useUploadDICOM,
} from '~/backend/connector-api/connector-components';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { environmentState } from '~/state/atoms/engineAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { handleError } from '~/utils/errors';
import {
    buildRegistrationFilename,
    buildRtDoseFilename,
    buildRtPlanFilename,
    buildStructureSetFilename,
} from '~/utils/io/files/dicomFilename';
import {
    downloadImageToDisk,
    saveBlobToFile,
} from '~/utils/io/files/downloadDicom';
import {
    buildOptimizationObjectivesCsv,
    buildOptimizationObjectivesFilename,
    buildOptimizationObjectivesJson,
} from '~/utils/io/files/optimizationObjectives';
import useSelectedPatient from '../patient/useSelectedPatient';

/**
 * Identifies a structure set to export and the optional subset of ROIs
 * to include. Keyed by SOP Instance UID — the natural key the
 * {@link StructureSetExportService} already works with. The live domain
 * entity is resolved inside the service, never handled by the hook.
 */
export type SelectedStructureSetsWithStructures = {
    /** SOP Instance UID of the structure set to export. */
    structureSetUid: string;
    /**
     * ROI numbers to include. When undefined the whole structure set is
     * exported.
     */
    selectedRoiNumbers?: number[];
};

/**
 * Extracts the DICOM dose and plan blobs from a DoseMatrix.
 */
const getDoseBlobs = (
    dose: DoseMatrix
): { doseBlob?: Blob; planBlob?: Blob } => ({
    doseBlob: dose.dicomDose.getAsDicomBlob(),
    planBlob: dose.plan?.dicom?.getAsDicomBlob(),
});

export const useDicomExport = () => {
    const { mutateAsync: uploadDICOM } = useUploadDICOM();
    const selectedPatient = useSelectedPatient();
    const [exporting, setExporting] = useState(false);
    const environment = useRecoilValue(environmentState);
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const session = useSessionOrNull();

    /**
     * Produces a DICOM RTSTRUCT blob for a structure set, optionally
     * limited to a subset of ROIs.
     *
     * Blob production is delegated to {@link StructureSetExportService};
     * the hook never touches the live `StructureSet` entity. The engine
     * → domain sync (`environment.saveChanges()`) stays here because it
     * is a UI/engine concern, not the service's responsibility.
     */
    const structureSetToBlob = (
        structureSetUid: string,
        selectedRoiNumbers?: number[],
        exportToMonaco?: boolean
    ): Blob => {
        if (!environment) {
            throw new Error('Internal error, Environment not found');
        }
        if (!session) {
            throw new Error('Internal error, patient session not found');
        }
        environment.saveChanges();
        return session.structureSetExportService.exportBlob(structureSetUid, {
            selectedRoiNumbers,
            characterSet: exportToMonaco ? 'ISO_IR 100' : undefined,
        });
    };

    /**
     * Produces a Monaco DIR file blob for a structure set.
     *
     * Exports the structure set in a format compatible with Monaco,
     * allowing for easy sharing and collaboration. Blob production is
     * delegated to {@link StructureSetExportService}.
     *
     * @param {string} structureSetUid - SOP Instance UID of the structure set.
     * @returns {Blob} A Blob representing the exported structure set in Monaco format.
     */
    const monacoStructureSetToBlob = (structureSetUid: string): Blob => {
        if (!environment) {
            throw new Error('Internal error, Environment not found');
        }
        if (!session) {
            throw new Error('Internal error, patient session not found');
        }
        environment.saveChanges();
        return session.structureSetExportService.exportBlob(structureSetUid, {
            asMonacoFile: true,
            patientName: selectedPatient?.dataSet.PatientName ?? 'NO_NAME',
        });
    };

    const uploadDicomBlob = async (
        blob: Blob,
        filename: string,
        targetName: string
    ): Promise<void> => {
        const formData = new FormData();
        formData.append('files', blob, filename);
        await uploadDICOM({
            body: formData as unknown as UploadDICOMRequestBody,
            pathParams: { target: targetName },
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    };

    /**
     * Downloads a DICOM Image as a ZIP file.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * combines them into a ZIP archive, and downloads it.
     *
     * @param {Image3D} image - The image to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadImage = async (image: Image3D): Promise<void> => {
        setExporting(true);
        await downloadImageToDisk(image);
        setExporting(false);
    };

    /**
     * Exports a DICOM Image by uploading all slices.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * and uploads them individually to the server.
     *
     * @param {Image3D} image - The image to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportImage = async (
        image: Image3D,
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);

            const blobs = Array.from(image.getSlicesAsDicomBlobs());

            for (let i = 0; i < blobs.length; i++) {
                const blob = blobs[i];
                await uploadDicomBlob(
                    blob,
                    `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`,
                    targetName
                );
            }

            image.setExported();

            enqueueSnackbar('DICOM image files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during image export:', error);
            enqueueSnackbar('Failed to upload image files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Resolves the structure set label used in export file names.
     *
     * The {@link StructureSetExportService} owns blob production but the
     * file name is a UI concern, so the hook reads the label from the
     * patient. This does not re-expose the live entity beyond a single
     * field read.
     */
    const getStructureSetLabel = (structureSetUid: string): string => {
        const ss =
            session?.patient.getStructureSetWithSopInstanceUid(structureSetUid);
        if (!ss) {
            throw new Error(`Structure set not found: ${structureSetUid}`);
        }
        return ss.structureSetLabel;
    };

    /**
     * Downloads a DICOM Structure Set file.
     *
     * This function exports a Structure Set either with a subset of selected ROIs
     * or the entire structure set, then saves it as a `.dcm` file.
     *
     * @param {string} structureSetUid - SOP Instance UID of the structure set to export.
     * @param {number[]} [selectedRoiNumbers] - Optional ROI numbers to include in the export.
     *                                  If omitted, the entire structure set is exported.
     * @returns {Promise<void>} A promise that resolves when the export process completes.
     */
    const handleDownloadStructureSet = async (
        structureSetUid: string,
        selectedRoiNumbers?: number[]
    ): Promise<void> => {
        try {
            setExporting(true);

            const structureSetLabel = getStructureSetLabel(structureSetUid);
            const structureSetBlob = structureSetToBlob(
                structureSetUid,
                selectedRoiNumbers
            );

            if (!structureSetBlob) {
                throw new Error('Failed to generate Structure Set Blob');
            }

            saveBlobToFile(
                structureSetBlob,
                buildStructureSetFilename(structureSetLabel, structureSetUid)
            );
        } catch (error) {
            handleError(error, 'Exporting files failed, please try again.');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Marks a structure set as exported via the tracked service so the
     * version-cached read models (export dialog isNew/isExported flags)
     * invalidate. A missing structure set throws — silently skipping
     * would leave an uploaded set presenting as never sent.
     */
    const markStructureSetExported = (structureSetUid: string): void => {
        const ss =
            session?.patient.getStructureSetWithSopInstanceUid(structureSetUid);
        if (!session || !ss) {
            throw new Error(`Structure set not found: ${structureSetUid}`);
        }
        session.structureSetService.markExported({ id: ss.id });
    };

    /**
     * Exports a Structure Set as a DICOM file and uploads it.
     *
     * This function converts a selected subset of ROIs from the structure set into a DICOM file,
     * packages it as FormData, and uploads it to the server.
     *
     * @param {string} structureSetUid - SOP Instance UID of the structure set to export.
     * @param {number[]} selectedRoiNumbers - ROI numbers to include in the export.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportStructureSet = async (
        structureSetUid: string,
        selectedRoiNumbers: number[],
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);

            const structureSetLabel = getStructureSetLabel(structureSetUid);
            const structureSetBlob = structureSetToBlob(
                structureSetUid,
                selectedRoiNumbers
            );

            if (!structureSetBlob) {
                throw new Error('Failed to generate Structure Set Blob');
            }

            const formData = new FormData();
            formData.append(
                'files',
                structureSetBlob,
                buildStructureSetFilename(structureSetLabel, structureSetUid)
            );

            await uploadDICOM({
                body: formData as unknown as UploadDICOMRequestBody,
                pathParams: { target: targetName },
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            markStructureSetExported(structureSetUid);

            setHasUnsavedChanges(false);
            enqueueSnackbar('DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during export:', error);
            enqueueSnackbar('Failed to upload DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a Dose Matrix as DICOM files and uploads them.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix,
     * packages them as FormData, and uploads them to the server.
     *
     * @param {DoseMatrix} dose - The dose matrix to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportDose = async (
        dose: DoseMatrix,
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (planBlob) {
                await uploadDicomBlob(
                    planBlob,
                    buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                    targetName
                );
            }
            if (doseBlob) {
                await uploadDicomBlob(
                    doseBlob,
                    buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                    targetName
                );
            }

            dose.setExported();
            dose.plan?.setExported();

            enqueueSnackbar('DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during export:', error);
            enqueueSnackbar('Failed to upload DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a Dose Matrix as DICOM files.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix
     * and saves them as `.dcm` files.
     *
     * @param {DoseMatrix} dose - The dose matrix to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadDose = async (dose: DoseMatrix): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (doseBlob) {
                saveBlobToFile(
                    doseBlob,
                    buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid)
                );
            }
            if (planBlob) {
                saveBlobToFile(
                    planBlob,
                    buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid)
                );
            }
        } catch (error) {
            handleError(
                error,
                'Downloading dose files failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a DICOM Registration file.
     *
     * @param {RigidRegistration} registration - The registration to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadRegistration = async (
        registration: RigidRegistration
    ): Promise<void> => {
        try {
            setExporting(true);
            registration.syncTransformationToDataset();
            const blob = registration.dicomParent.getAsDicomBlob();
            const label = registration.contentLabel || registration.dicomUid;
            saveBlobToFile(blob, buildRegistrationFilename(label));
        } catch (error) {
            handleError(
                error,
                'Downloading registration file failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a DICOM Registration file and uploads it.
     *
     * @param {RigidRegistration} registration - The registration to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportRegistration = async (
        registration: RigidRegistration,
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);
            registration.syncTransformationToDataset();
            const blob = registration.dicomParent.getAsDicomBlob();
            const label = registration.contentLabel || registration.dicomUid;
            await uploadDicomBlob(
                blob,
                buildRegistrationFilename(label),
                targetName
            );
            registration.setExported();
            enqueueSnackbar('DICOM registration file uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during registration export:', error);
            enqueueSnackbar('Failed to upload registration file', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` as a batch.
     *
     * If there's only one item, it will download it directly.
     * If there are multiple items, it will combine them into a ZIP archive.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects identifying a structure set by SOP UID and its selected ROI numbers.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be downloaded.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be downloaded.
     * @returns {Promise<void>} A promise that resolves when all downloads are completed.
     */
    const handleDownloadBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        exportToMonaco: boolean = false,
        selectedRegistrations: RigidRegistration[] = []
    ): Promise<void> => {
        try {
            setExporting(true);
            const totalItems =
                selectedStructureSets.length +
                selectedDoses.length +
                selectedImages.length +
                selectedRegistrations.length;

            // Single-dose-with-objectives must go through the zip path
            // because the JSON sidecar would otherwise trigger a separate
            // browser download alongside the .dcm files (and some browsers
            // block multiple consecutive saves).
            const singleDoseHasObjectives =
                selectedDoses.length === 1 &&
                (selectedDoses[0].optimizationObjectives?.length ?? 0) > 0;

            if (totalItems === 1 && !singleDoseHasObjectives) {
                if (selectedImages.length === 1) {
                    return await handleDownloadImage(selectedImages[0]);
                } else if (
                    selectedStructureSets.length === 1 &&
                    !exportToMonaco
                ) {
                    const { structureSetUid, selectedRoiNumbers } =
                        selectedStructureSets[0];
                    return await handleDownloadStructureSet(
                        structureSetUid,
                        selectedRoiNumbers
                    );
                } else if (selectedDoses.length === 1) {
                    return await handleDownloadDose(selectedDoses[0]);
                } else if (selectedRegistrations.length === 1) {
                    return await handleDownloadRegistration(
                        selectedRegistrations[0]
                    );
                }
            }

            const timestamp = new Date()
                .toISOString()
                .replace(/[:.]/g, '-')
                .slice(0, -5);
            const filename = `dicom-export-${timestamp}`;

            const zip = new JSZip();

            for (const image of selectedImages) {
                const dir = `${image.seriesMetadata.modality}_${image.dicomUid}`;
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                blobs.forEach((blob, index) => {
                    zip.file(`${dir}/${index}.dcm`, blob);
                });
            }

            for (const {
                structureSetUid,
                selectedRoiNumbers,
            } of selectedStructureSets) {
                const structureSetLabel = getStructureSetLabel(structureSetUid);
                const blob = structureSetToBlob(
                    structureSetUid,
                    selectedRoiNumbers,
                    exportToMonaco
                );
                if (blob) {
                    zip.file(
                        buildStructureSetFilename(
                            structureSetLabel,
                            structureSetUid
                        ),
                        blob
                    );
                }

                if (exportToMonaco) {
                    const monacoBlob =
                        monacoStructureSetToBlob(structureSetUid);
                    if (monacoBlob) {
                        const monacoFilename = buildStructureSetFilename(
                            structureSetLabel,
                            structureSetUid
                        ).replace(/\.dcm$/, '.dir');
                        zip.file(monacoFilename, monacoBlob);
                    }
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (doseBlob) {
                    zip.file(
                        buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                        doseBlob
                    );
                }
                if (planBlob) {
                    zip.file(
                        buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                        planBlob
                    );
                }
                if (
                    dose.optimizationObjectives &&
                    dose.optimizationObjectives.length > 0
                ) {
                    const json = buildOptimizationObjectivesJson(
                        dose.optimizationObjectives
                    );
                    zip.file(
                        buildOptimizationObjectivesFilename(
                            dose.plan?.label,
                            dose.dicomUid
                        ),
                        JSON.stringify(json, null, 2)
                    );
                    const csv = buildOptimizationObjectivesCsv(
                        dose.optimizationObjectives
                    );
                    zip.file(
                        buildOptimizationObjectivesFilename(
                            dose.plan?.label,
                            dose.dicomUid,
                            'csv'
                        ),
                        csv
                    );
                }
            }

            for (const registration of selectedRegistrations) {
                registration.syncTransformationToDataset();
                const blob = registration.dicomParent.getAsDicomBlob();
                const label =
                    registration.contentLabel || registration.dicomUid;
                zip.file(buildRegistrationFilename(label), blob);
            }

            // Generate and download the ZIP file
            const zipBlob = await zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
            });
            saveBlobToFile(zipBlob, `${filename}.zip`);
        } catch (error) {
            handleError(error, 'Failed to download DICOM files');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` in a single batch upload.
     *
     * This function processes multiple objects,
     * extracts the corresponding DICOM files, and uploads them all in separate API requests.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects identifying a structure set by SOP UID and its selected ROI numbers.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be exported.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload process is completed.
     */
    const handleExportBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        targetName: string,
        selectedRegistrations: RigidRegistration[] = []
    ) => {
        try {
            setExporting(true);

            for (const image of selectedImages) {
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                for (let i = 0; i < blobs.length; i++) {
                    const blob = blobs[i];
                    await uploadDicomBlob(
                        blob,
                        `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`,
                        targetName
                    );
                }
            }

            for (const {
                structureSetUid,
                selectedRoiNumbers,
            } of selectedStructureSets) {
                const structureSetLabel = getStructureSetLabel(structureSetUid);
                const blob = structureSetToBlob(
                    structureSetUid,
                    selectedRoiNumbers
                );
                if (blob) {
                    await uploadDicomBlob(
                        blob,
                        buildStructureSetFilename(
                            structureSetLabel,
                            structureSetUid
                        ),
                        targetName
                    );
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (planBlob) {
                    await uploadDicomBlob(
                        planBlob,
                        buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                        targetName
                    );
                }
                if (doseBlob) {
                    await uploadDicomBlob(
                        doseBlob,
                        buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                        targetName
                    );
                }
            }

            for (const registration of selectedRegistrations) {
                registration.syncTransformationToDataset();
                const blob = registration.dicomParent.getAsDicomBlob();
                const label =
                    registration.contentLabel || registration.dicomUid;
                await uploadDicomBlob(
                    blob,
                    buildRegistrationFilename(label),
                    targetName
                );
            }

            selectedImages.forEach((image) => {
                image.setExported();
            });

            selectedStructureSets.forEach(({ structureSetUid }) => {
                markStructureSetExported(structureSetUid);
            });

            selectedDoses.forEach((dose) => {
                dose.setExported();
                dose.plan?.setExported();
            });

            selectedRegistrations.forEach((registration) => {
                registration.setExported();
            });

            setHasUnsavedChanges(false);
            enqueueSnackbar('All selected DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during batch export:', error);
            enqueueSnackbar('Failed to upload batch DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };
    return {
        exporting,
        handleDownloadStructureSet,
        handleExportImage,
        handleExportStructureSet,
        handleExportDose,
        handleDownloadDose,
        handleDownloadRegistration,
        handleExportRegistration,
        handleDownloadBatch,
        handleExportBatch,
    };
};
